/**
 * UserSettingsPage
 * Allows users to manage their preferences: language, timezone, date format,
 * landing page, and email opt-out.
 */

import React, { forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { PortalLayout } from '@/components/common/PortalLayout';
import { useUserSettings } from './UserSettingsPage.hook';
import type { UserPreferences } from '@/application/queries/userPreferences';

// ============================================================================
// Constants
// ============================================================================

const LANGUAGES = [
  { code: 'en-US', label: 'English (en-US)' },
  { code: 'vi-VN', label: 'Tiếng Việt (vi-VN)' },
];

const DATE_FORMATS = [
  { value: '', label: 'System Default' },
  { value: 'MM/dd/yyyy', label: 'MM/dd/yyyy (01/15/2026)' },
  { value: 'dd/MM/yyyy', label: 'dd/MM/yyyy (15/01/2026)' },
  { value: 'yyyy-MM-dd', label: 'yyyy-MM-dd (2026-01-15)' },
];

const LANDING_PAGES = [
  { controller: 'Home', action: 'Dashboard', label: 'Dashboard' },
  { controller: 'Mailbox', action: 'MailboxList', label: 'Mailbox List' },
  { controller: 'Transaction', action: 'Transactions', label: 'Transactions' },
];

const COMMON_TIMEZONES = [
  { value: 'Browser/Default', label: 'Browser Default' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Asia/Bangkok', label: 'Indochina Time (ICT, Bangkok)' },
  { value: 'Asia/Singapore', label: 'Singapore Time (SGT)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Europe/London', label: 'GMT / British Time' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
];

// ============================================================================
// Field row helper
// ============================================================================

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-2 items-start py-4 border-b border-border last:border-0">
    <Label className="text-sm font-medium pt-2">{label}</Label>
    <div>{children}</div>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

export const UserSettingsPage = forwardRef<HTMLDivElement>((_, ref) => {
  const { t } = useTranslation(NS.USER_SETTINGS);
  const { data, loading, error, saving, saveError, saveSuccess, savePreferences } =
    useUserSettings();

  // Local form state (mirrors preferences, updated on load)
  const [form, setForm] = React.useState<UserPreferences>({
    language: '',
    timezone: 'Browser/Default',
    timezoneOffset: -999,
    dateFormat: '',
    landingPageController: 'Home',
    landingPageAction: 'Dashboard',
    emailOptout: false,
    uiMode: 'mvc',
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    savePreferences(form);
  };

  const landingKey = `${form.landingPageController}/${form.landingPageAction}`;

  // ── Loading ──
  if (loading) {
    return (
      <PortalLayout ref={ref}>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          {t('UserSettings_Loading', 'Loading preferences...')}
        </div>
      </PortalLayout>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <PortalLayout ref={ref}>
        <div className="max-w-lg mx-auto py-12 text-center">
          <p className="text-destructive text-sm mb-4">
            {t('UserSettings_Error', 'Failed to load preferences.')}
          </p>
        </div>
      </PortalLayout>
    );
  }

  // ── Page ──
  return (
    <PortalLayout ref={ref}>
      <div className="max-w-2xl mx-auto py-8 px-4">
        <h1 className="text-xl font-semibold mb-1">{t('UserSettings_Title', 'User Preferences')}</h1>
        <p className="text-sm text-muted-foreground mb-8">
          {t('UserSettings_Description', 'Customize your portal experience.')}
        </p>

        <form onSubmit={handleSave}>
          <div className="rounded-lg border border-border bg-card p-6">
            {/* Language */}
            <FieldRow label={t('UserSettings_Language', 'Language')}>
              <Select
                value={form.language || 'en-US'}
                onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}
              >
                <SelectTrigger className="w-full max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.language !== data?.language && form.language && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('UserSettings_LanguageNote', 'Page will reload to apply new language.')}
                </p>
              )}
            </FieldRow>

            {/* Timezone */}
            <FieldRow label={t('UserSettings_Timezone', 'Timezone')}>
              <Select
                value={form.timezone || 'Browser/Default'}
                onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}
              >
                <SelectTrigger className="w-full max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

            {/* Date Format */}
            <FieldRow label={t('UserSettings_DateFormat', 'Date Format')}>
              <Select
                value={form.dateFormat || ''}
                onValueChange={(v) => setForm((f) => ({ ...f, dateFormat: v }))}
              >
                <SelectTrigger className="w-full max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_FORMATS.map((df) => (
                    <SelectItem key={df.value} value={df.value}>
                      {df.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

            {/* Landing Page */}
            <FieldRow label={t('UserSettings_LandingPage', 'Landing Page')}>
              <Select
                value={landingKey}
                onValueChange={(v) => {
                  const [ctrl, action] = v.split('/');
                  setForm((f) => ({ ...f, landingPageController: ctrl, landingPageAction: action }));
                }}
              >
                <SelectTrigger className="w-full max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANDING_PAGES.map((p) => (
                    <SelectItem key={`${p.controller}/${p.action}`} value={`${p.controller}/${p.action}`}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>

            {/* Email Opt-out */}
            <FieldRow label={t('UserSettings_EmailOptout', 'Email Notifications')}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!form.emailOptout}
                  onChange={(e) => setForm((f) => ({ ...f, emailOptout: !e.target.checked }))}
                  className="rounded border-border"
                />
                <span className="text-sm">
                  {t('UserSettings_EmailOptout_Subscribe', 'Receive portal email notifications')}
                </span>
              </label>
            </FieldRow>
          </div>

          {/* Feedback */}
          {saveError && (
            <p className="text-destructive text-sm mt-4">
              {t('UserSettings_SaveError', 'Failed to save preferences. Please try again.')}
            </p>
          )}
          {saveSuccess && !saveError && (
            <p className="text-green-600 text-sm mt-4">
              {t('UserSettings_SaveSuccess', 'Preferences saved successfully.')}
            </p>
          )}

          <div className="flex justify-end mt-6">
            <Button type="submit" disabled={saving}>
              {saving
                ? t('UserSettings_Saving', 'Saving...')
                : t('UserSettings_Save', 'Save Preferences')}
            </Button>
          </div>
        </form>
      </div>
    </PortalLayout>
  );
});

UserSettingsPage.displayName = 'UserSettingsPage';
